import {
  html,
  SYSBaseElement,
  TemplateResult,
} from "../../../../../../_ovlclient/src"
import { T } from "../../../../../../_ovlclient/src/textapi"
import { FeatureGet } from "../../../../../../_ovlclient/src/global/globalapi"
import {
  ProcessStart,
  ProcessEnd,
  ProcessIsRunning,
} from "../../../../../../_ovlclient/src/library/longrunningprocesstracker/processapi"
import type { KPIProps } from "./kpicomponent"

export interface KPIContainerProps {
  type: "Dashboard" | "All"
  filterCardCode?: string
}

interface KPIUserConfig {
  enabled: boolean
  order: number
  showIndustryAvg?: boolean
  showPreviousYear?: boolean
  useDemoData?: boolean
}

export class CompKpiContainer extends SYSBaseElement {
  props!: KPIContainerProps

  async refresh() {
    if (ProcessIsRunning("KPIDataRefresh")) return
    await this.actions.app.features.KPI.actions.FetchAllKPIData({
      viewType: this.props.type,
      filterCardCode: this.props.filterCardCode,
    })
  }

  getGroupedKPIs(kpiConfigs: {
    [viewType: string]: { [kpiCode: string]: KPIUserConfig }
  }) {
    const viewConfig = kpiConfigs[this.props.type]
    if (!viewConfig) return {}

    const kpiCodes = Object.keys(viewConfig).filter(
      (code) => viewConfig[code].enabled
    )

    // Grouping logic: We split by prefix (e.g., KPIMaterial, KPIGenerell, KPIBearbeitung)
    // and provide a translation for the group.
    const groups: { [key: string]: string[] } = {}

    kpiCodes.forEach((code) => {
      let groupKey = "Other"
      if (code.startsWith("KPIMaterial_")) groupKey = "Material"
      else if (code.startsWith("KPIGenerell_")) groupKey = "General"
      else if (code.startsWith("KPIBearbeitung_")) groupKey = "Processing"

      if (!groups[groupKey]) groups[groupKey] = []
      groups[groupKey].push(code)
    })

    // Sort within groups by 'order'
    Object.keys(groups).forEach((key) => {
      groups[key].sort(
        (a, b) => (viewConfig[a].order || 0) - (viewConfig[b].order || 0)
      )
    })

    return groups
  }

  async getUI(): Promise<TemplateResult> {
    const kpiFeature = FeatureGet("KPIUser")
    if (!kpiFeature.Found()) {
      return html`<div></div>`
    }

    const kpiConfigs = kpiFeature.GetJson1Parsed() as {
      [viewType: string]: { [kpiCode: string]: KPIUserConfig }
    }
    if (!kpiConfigs) {
      return html`<div></div>`
    }

    const viewConfig = kpiConfigs[this.props.type]
    if (!viewConfig) {
      return html`<div></div>`
    }

    const groupedKPIs = this.getGroupedKPIs(kpiConfigs)
    const groupKeys = Object.keys(groupedKPIs).sort((groupA, groupB) => {
      const minOrderA = Math.min(...groupedKPIs[groupA].map((code) => viewConfig[code].order || 0))
      const minOrderB = Math.min(...groupedKPIs[groupB].map((code) => viewConfig[code].order || 0))
      return minOrderA - minOrderB
    })

    if (groupKeys.length === 0) {
      return html`<div></div>`
    }

    return html`
      <div class="kpi-container-main">
        <div class="kpi-container-header">
          <h2 class="fd-has-type-2">
            ${T(
              this.props.type === "Dashboard"
                ? "KPIDashboardTitle"
                : "KPIAllTitle"
            )}
          </h2>
          <button
            class="fd-button fd-button--transparent"
            @click=${() => this.refresh()}
            ?disabled=${ProcessIsRunning("KPIDataRefresh")}
            aria-label="Refresh"
          >
            <i class="sap-icon--refresh"></i>
          </button>
        </div>

        ${groupKeys.map(
          (groupKey) => html`
            <div class="kpi-group">
              <div class="kpi-group-header">
                <h3 class="kpi-group-title">
                  ${T("KPIGroup_" + groupKey)}
                </h3>
              </div>
              <div class="dash-kpi-container">
                ${groupedKPIs[groupKey].map(
                  (code) => html`
                    <comp-kpi
                      .props=${{
                        kpiCode: code,
                        viewType: this.props.type,
                        filterCardCode: this.props.filterCardCode,
                      } as KPIProps}
                    ></comp-kpi>
                  `
                )}
              </div>
            </div>
          `
        )}
      </div>
    `
  }
}
